import type { MapDef } from "../data/types";
import { clamp } from "./math";

// The static map: a tile heightfield with metal spots and reclaimable features.
// Elevation is generated deterministically from the JSON hill list, so the map
// is fully data-defined and (by construction of map_duel.json) mirror-symmetric.

export class GameMap {
  readonly def: MapDef;
  readonly cols: number;
  readonly rows: number;
  readonly cellSize: number;
  readonly worldW: number;
  readonly worldH: number;
  readonly heights: Float32Array; // 0..1 per cell
  readonly walkable: Uint8Array; // 1 = walkable static terrain

  constructor(def: MapDef) {
    this.def = def;
    this.cols = def.cols;
    this.rows = def.rows;
    this.cellSize = def.cellSize;
    this.worldW = def.cols * def.cellSize;
    this.worldH = def.rows * def.cellSize;
    this.heights = new Float32Array(def.cols * def.rows);
    this.walkable = new Uint8Array(def.cols * def.rows);
    this.buildHeightfield();
  }

  private buildHeightfield(): void {
    const { cols, rows, cellSize } = this;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const wx = (c + 0.5) * cellSize;
        const wy = (r + 0.5) * cellSize;
        let h = 0;
        for (const hill of this.def.hills) {
          const dx = wx - hill.x;
          const dy = wy - hill.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < hill.radius) {
            // Smooth cosine falloff, contributions combine by max (plateaus, not stacks).
            const t = 1 - d / hill.radius;
            const contrib = hill.height * (0.5 - 0.5 * Math.cos(Math.PI * t));
            if (contrib > h) h = contrib;
          }
        }
        const idx = r * cols + c;
        this.heights[idx] = h;
        // A 1-cell border wall keeps units on the field; everything else walks.
        this.walkable[idx] = c === 0 || r === 0 || c === cols - 1 || r === rows - 1 ? 0 : 1;
      }
    }
  }

  cellIndex(cx: number, cy: number): number {
    return cy * this.cols + cx;
  }

  worldToCellX(x: number): number {
    return clamp((x / this.cellSize) | 0, 0, this.cols - 1);
  }

  worldToCellY(y: number): number {
    return clamp((y / this.cellSize) | 0, 0, this.rows - 1);
  }

  isWalkable(cx: number, cy: number): boolean {
    if (cx < 0 || cy < 0 || cx >= this.cols || cy >= this.rows) return false;
    return this.walkable[cy * this.cols + cx] === 1;
  }

  /** Bilinear elevation sample in [0,1] at world coords. */
  elevationAt(x: number, y: number): number {
    const fx = clamp(x / this.cellSize - 0.5, 0, this.cols - 1.001);
    const fy = clamp(y / this.cellSize - 0.5, 0, this.rows - 1.001);
    const c0 = fx | 0;
    const r0 = fy | 0;
    const tx = fx - c0;
    const ty = fy - r0;
    const h00 = this.heights[r0 * this.cols + c0];
    const h10 = this.heights[r0 * this.cols + c0 + 1];
    const h01 = this.heights[(r0 + 1) * this.cols + c0];
    const h11 = this.heights[(r0 + 1) * this.cols + c0 + 1];
    const a = h00 + (h10 - h00) * tx;
    const b = h01 + (h11 - h01) * tx;
    return a + (b - a) * ty;
  }

  clampX(x: number): number {
    return clamp(x, this.cellSize * 1.1, this.worldW - this.cellSize * 1.1);
  }

  clampY(y: number): number {
    return clamp(y, this.cellSize * 1.1, this.worldH - this.cellSize * 1.1);
  }
}
